import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { Driver } from '../models/Driver';
import { DriverImport } from '../models/DriverImport';
import { Params } from '@angular/router';
import { FiltersParserService } from './filters-parser.service';
import { CollectionUtils } from '../utils/CollectionUtils';

@Injectable({
  providedIn: 'root',
})
export class DriverService {
  private loading = new BehaviorSubject(false);
  readonly loading$ = this.loading.asObservable();

  constructor(private readonly httpClient: HttpClient, private readonly filtersParser: FiltersParserService) {}

  all(): Observable<Driver[]> {
    this.loading.next(true);
    return this.httpClient.get<Driver[]>('/accounts/users/me/drivers').pipe(
      tap((results) => CollectionUtils.orderByProperty(results, 'lastName')),
      finalize(() => this.loading.next(false))
    );
  }

  findFiltered(params: Params): Observable<Driver[]> {
    return this.all().pipe(map((drivers) => {
      const filtered = this.filterDrivers(drivers, params);
      return CollectionUtils.orderByProperty(filtered, 'lastName');
    }));
  }

  private filterDrivers(drivers: Driver[], params: Params): Driver[] {
    if (Object.keys(params).length === 0) {
      return drivers;
    }
    const filters = this.filtersParser.parseFilters(params.filters || '') as { show: ('active' | 'inactive')[] };

    if (filters?.show?.length ?? false) {
      drivers = drivers.filter(
        (driver) =>
          (filters.show.includes('active') && !driver?.account?.pending) || (filters.show.includes('inactive') && driver?.account?.pending)
      );
    }

    return drivers.filter((driver) => {
      const searchText = params.searchText?.toLowerCase() || '';
      return (
        driver.firstName?.toLowerCase().includes(searchText) ||
        driver.lastName?.toLowerCase().includes(searchText) ||
        driver.email?.toLowerCase().includes(searchText) ||
        JSON.stringify(Object.values(driver?.account ?? {}))
          .toLowerCase()
          .includes(searchText) ||
        driver.phoneNumber?.toLowerCase().includes(searchText)
      );
    });
  }

  get(driverId: string): Observable<Driver> {
    this.loading.next(true);
    return this.httpClient.get<Driver>(`/accounts/users/me/drivers/${driverId}`).pipe(finalize(() => this.loading.next(false)));
  }

  post(driver: Omit<Driver, 'driverId'>): Observable<Pick<Driver, 'driverId'>> {
    this.loading.next(true);
    return this.httpClient
      .post<Pick<Driver, 'driverId'>>('/accounts/users/me/drivers', driver)
      .pipe(finalize(() => this.loading.next(false)));
  }

  importDrivers(drivers: DriverImport[]): Observable<object> {
    this.loading.next(true);
    return this.httpClient
      .post<object>('/accounts/users/me/drivers/import', drivers)
      .pipe(finalize(() => this.loading.next(false)));
  }

  put(driver: Driver): Observable<void> {
    this.loading.next(true);
    if(driver.account) driver.username = driver.account.username
    return this.httpClient
      .put<void>(`/accounts/users/me/drivers/${driver.driverId}`, driver)
      .pipe(finalize(() => this.loading.next(false)));
  }

  delete(id: number): Observable<void> {
    this.loading.next(true);
    return this.httpClient.delete<void>(`/accounts/users/me/drivers/${id}`).pipe(finalize(() => this.loading.next(false)));
  }
}
